import NiceModal, { useModal } from "@ebay/nice-modal-react"
import cx from "classnames"
import { Check } from "lucide-react"
import { useMemo, useState } from "react"
import { ACTION_COLOR_STYLES } from "../../actions/actionStyles"
import { SAVED_ACTION_ICON_PRESETS, savedActionIcon } from "../../actions/actionIcons"
import {
    ACTION_COLORS,
    ACTION_VISIBILITY_CONDITIONS,
    ACTION_VISIBILITY_MISMATCH_BEHAVIORS,
    type SavedActionDraft,
    type SavedActionVisibilityCondition,
} from "../../actions/actionTypes"
import type { SavedActionStore } from "../../persistence/savedActionStore"
import { Modal } from "../ui/Modal"
import { Switch } from "../ui/Switch"

const CONDITION_LABELS: Record<SavedActionVisibilityCondition, { label: string; description: string }> = {
    "task-idle": {
        label: "Task is idle",
        description: "No turn is currently running.",
    },
    "task-running": {
        label: "Task is running",
        description: "A turn is active or stopping.",
    },
    "git-repository": {
        label: "Git repository",
        description: "The task has a working directory.",
    },
    "git-working-changes": {
        label: "Working changes",
        description: "Git reports uncommitted changes.",
    },
    "git-unpushed-commits": {
        label: "Unpushed commits",
        description: "The branch is ahead of its upstream.",
    },
}

const EMPTY_DRAFT: SavedActionDraft = {
    name: "",
    prompt: "",
    icon: "sparkles",
    color: "blue",
    requiresConfirmation: false,
    allowCombining: true,
    visibility: {
        match: "all",
        conditions: [],
        onMismatch: "disable",
    },
}

interface ActionEditorModalProps {
    actionId?: string
    duplicateFromActionId?: string
    actions: SavedActionStore
}

export const ActionEditorModal = NiceModal.create(({ actionId, duplicateFromActionId, actions }: ActionEditorModalProps) => {
    const modal = useModal()
    const initialDraft = useMemo(() => {
        const action = actions.getAction(actionId ?? duplicateFromActionId ?? "")
        if (!action) return EMPTY_DRAFT
        return {
            name: duplicateFromActionId ? `${action.name} copy` : action.name,
            prompt: action.prompt,
            icon: action.icon,
            color: action.color,
            requiresConfirmation: action.requiresConfirmation,
            allowCombining: action.sessionLaunch ? false : action.allowCombining,
            visibility: {
                match: action.visibility.match,
                conditions: [...action.visibility.conditions],
                onMismatch: action.visibility.onMismatch,
            },
            ...(action.sessionLaunch ? { sessionLaunch: { ...action.sessionLaunch } } : {}),
        } satisfies SavedActionDraft
    }, [actionId, actions, duplicateFromActionId])
    const [draft, setDraft] = useState<SavedActionDraft>(initialDraft)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const canSave = draft.name.trim().length > 0 && draft.prompt.trim().length > 0 && !saving
    const PreviewIcon = savedActionIcon(draft.icon)

    const update = <K extends keyof SavedActionDraft>(key: K, value: SavedActionDraft[K]) => {
        setDraft((current) => ({ ...current, [key]: value }))
    }
    const toggleCondition = (condition: SavedActionVisibilityCondition) => {
        setDraft((current) => {
            const conditions = current.visibility.conditions.includes(condition)
                ? current.visibility.conditions.filter((candidate) => candidate !== condition)
                : [...current.visibility.conditions, condition]
            return {
                ...current,
                visibility: { ...current.visibility, conditions },
            }
        })
    }
    const save = async () => {
        if (!canSave) return
        setSaving(true)
        setError(null)
        try {
            const normalized: SavedActionDraft = {
                ...draft,
                name: draft.name.trim(),
                prompt: draft.prompt.trim(),
            }
            if (actionId) await actions.updateAction(actionId, normalized)
            else await actions.createAction(normalized)
            modal.remove()
        } catch (saveError) {
            setError(saveError instanceof Error ? saveError.message : "Could not save the Action")
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal
            title={actionId ? "Edit Action" : duplicateFromActionId ? "Duplicate Action" : "New Action"}
            size="lg"
            footer={
                <div className="flex items-center justify-end gap-2">
                    <button type="button" className="btn h-9 px-4 text-sm text-muted hover:bg-base-200" onClick={() => modal.remove()}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn h-9 bg-primary px-4 text-sm font-medium text-primary-content hover:bg-primary/80 disabled:cursor-not-allowed disabled:opacity-40"
                        disabled={!canSave}
                        onClick={() => void save()}
                    >
                        {saving ? "Saving…" : "Save Action"}
                    </button>
                </div>
            }
        >
            <div className="flex min-w-0 flex-col gap-5">
                <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-base-content">Button name</span>
                    <input
                        type="text"
                        value={draft.name}
                        maxLength={80}
                        onChange={(event) => update("name", event.target.value)}
                        placeholder="Run tests"
                        className="h-10 border border-border bg-input px-3 text-sm text-base-content outline-none focus:border-primary"
                        autoFocus
                    />
                </label>

                <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium text-base-content">Message</span>
                    <span className="text-xs text-muted">Sent as its own queued user message. Be explicit about preconditions and success criteria.</span>
                    <textarea
                        value={draft.prompt}
                        maxLength={50_000}
                        onChange={(event) => update("prompt", event.target.value)}
                        placeholder="Run the relevant tests. Fix any failures caused by the current changes, then summarize what passed."
                        className="min-h-44 resize-y border border-border bg-input px-3 py-2 font-mono text-sm leading-5 text-base-content outline-none focus:border-primary"
                    />
                </label>

                <div className="flex items-center justify-between gap-4 border border-border bg-base-200/40 p-3">
                    <div>
                        <div className="text-sm font-medium text-base-content">Require confirmation</div>
                        <div className="text-xs text-muted">Ask before dispatching Actions that may be destructive or difficult to reverse.</div>
                    </div>
                    <Switch
                        checked={draft.requiresConfirmation}
                        onCheckedChange={(requiresConfirmation) => update("requiresConfirmation", requiresConfirmation)}
                        aria-label="Require confirmation before running"
                    />
                </div>

                <div className="flex items-center justify-between gap-4 border border-border bg-base-200/40 p-3">
                    <div>
                        <div className="text-sm font-medium text-base-content">Allow combining</div>
                        <div className="text-xs text-muted">
                            Merge adjacent compatible Actions into one queued turn. Turn this off when the Action needs its own turn.
                        </div>
                    </div>
                    <Switch
                        checked={draft.allowCombining}
                        disabled={Boolean(draft.sessionLaunch)}
                        onCheckedChange={(allowCombining) => update("allowCombining", allowCombining)}
                        aria-label="Allow combining with other Actions"
                    />
                </div>

                <section className="flex flex-col gap-3 border-t border-border pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm font-medium text-base-content">Appearance</div>
                        <div className={cx("flex h-9 items-center gap-2 px-3 text-sm font-semibold", ACTION_COLOR_STYLES[draft.color].swatch)}>
                            <PreviewIcon size={14} />
                            {draft.name.trim() || "Action"}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                        <div className="flex items-center gap-2">
                            <span className="w-10 text-xs text-muted">Color</span>
                            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Action color">
                                {ACTION_COLORS.map((color) => (
                                    <button
                                        key={color}
                                        type="button"
                                        role="radio"
                                        aria-checked={draft.color === color}
                                        className={cx(
                                            "btn flex h-8 w-8 items-center justify-center border-2",
                                            ACTION_COLOR_STYLES[color].swatch,
                                            draft.color === color ? "border-base-content" : "border-transparent"
                                        )}
                                        onClick={() => update("color", color)}
                                        title={ACTION_COLOR_STYLES[color].label}
                                        aria-label={ACTION_COLOR_STYLES[color].label}
                                    >
                                        {draft.color === color && <Check size={13} />}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex min-w-0 items-center gap-2">
                            <span className="w-10 text-xs text-muted">Icon</span>
                            <div className="flex flex-wrap gap-1.5">
                                {SAVED_ACTION_ICON_PRESETS.map((preset) => {
                                    const Icon = preset.icon
                                    return (
                                        <button
                                            key={preset.id}
                                            type="button"
                                            className={cx(
                                                "btn flex h-8 w-8 items-center justify-center border",
                                                draft.icon === preset.id
                                                    ? "border-primary bg-primary/10 text-primary"
                                                    : "border-border text-muted hover:bg-base-200 hover:text-base-content"
                                            )}
                                            onClick={() => update("icon", preset.id)}
                                            title={preset.label}
                                            aria-label={preset.label}
                                        >
                                            <Icon size={14} />
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="flex flex-col gap-3 border-t border-border pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <div className="text-sm font-medium text-base-content">Constraints</div>
                            <div className="text-xs text-muted">No constraints means the Action is always available.</div>
                        </div>
                        {draft.visibility.conditions.length > 1 && (
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-muted">Match</span>
                                <div className="flex border border-border">
                                    {(["all", "any"] as const).map((match) => (
                                        <button
                                            key={match}
                                            type="button"
                                            className={cx(
                                                "btn h-8 px-3 capitalize",
                                                draft.visibility.match === match ? "bg-primary text-primary-content" : "text-muted hover:bg-base-200"
                                            )}
                                            onClick={() =>
                                                setDraft((current) => ({
                                                    ...current,
                                                    visibility: { ...current.visibility, match },
                                                }))
                                            }
                                        >
                                            {match}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {ACTION_VISIBILITY_CONDITIONS.map((condition) => {
                            const checked = draft.visibility.conditions.includes(condition)
                            const copy = CONDITION_LABELS[condition]
                            return (
                                <button
                                    key={condition}
                                    type="button"
                                    className={cx(
                                        "btn flex h-9 items-center gap-2 border px-3 text-left",
                                        checked ? "border-primary bg-primary/10" : "border-border bg-base-100 hover:bg-base-200"
                                    )}
                                    onClick={() => toggleCondition(condition)}
                                    title={copy.description}
                                >
                                    <span
                                        className={cx(
                                            "flex h-4 w-4 shrink-0 items-center justify-center border",
                                            checked ? "border-primary bg-primary text-primary-content" : "border-border"
                                        )}
                                    >
                                        {checked && <Check size={11} />}
                                    </span>
                                    <span className="text-xs font-medium text-base-content">{copy.label}</span>
                                </button>
                            )
                        })}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-base-200/40 p-3">
                        <div>
                            <div className="text-sm font-medium text-base-content">When constraints don’t match</div>
                            <div className="text-xs text-muted">Disable keeps the button in place. Hide removes it.</div>
                        </div>
                        <div className="flex border border-border">
                            {ACTION_VISIBILITY_MISMATCH_BEHAVIORS.map((onMismatch) => (
                                <button
                                    key={onMismatch}
                                    type="button"
                                    className={cx(
                                        "btn h-8 px-3 capitalize",
                                        draft.visibility.onMismatch === onMismatch
                                            ? "bg-primary text-primary-content"
                                            : "bg-base-100 text-muted hover:bg-base-200"
                                    )}
                                    onClick={() =>
                                        setDraft((current) => ({
                                            ...current,
                                            visibility: { ...current.visibility, onMismatch },
                                        }))
                                    }
                                    aria-pressed={draft.visibility.onMismatch === onMismatch}
                                >
                                    {onMismatch}
                                </button>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
            {error && <div className="mt-4 border border-error/40 bg-error/10 p-3 text-sm text-error">{error}</div>}
        </Modal>
    )
})
